import {
  Button,
  FileInputControlled,
  Modal,
  useMobile,
  addToast,
} from '@octano/global-ui';
import {
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  Ref,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../locales/translations';
import { useForm } from 'react-hook-form';
import { useValidations } from '../../../../hooks/useValidations';
import { uploadData } from '../api';
import { downloadFromBlob } from '../../../../utils/blob';

export type AssignTutorModalMethods = {
  open: () => void;
  close: () => void;
};

type AssignTutorModalProps = {
  onUpload?: () => void;
};

type AssignTutorFields = {
  file: File | null;
};

const AssignTutorModal = (
  { onUpload }: AssignTutorModalProps,
  ref: Ref<AssignTutorModalMethods>,
) => {
  const isMobile = useMobile();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);

  const { msgValidations } = useValidations();

  const { control, handleSubmit, reset } = useForm<AssignTutorFields>({
    defaultValues: { file: null },
  });

  const { t } = useTranslation(
    TranslationsKeys.TUTOR_MANAGEMENT_TUTOR_ASSIGNMENT,
  );
  const prefix = 'modal';

  const close = useCallback(() => {
    if (uploading) {
      return;
    }
    setIsOpen(false);
  }, [uploading]);

  const open = useCallback(() => {
    if (uploading) {
      return;
    }
    reset({ file: null });
    setIsOpen(true);
  }, [uploading, reset]);

  const handleConfirm = useCallback(
    async (values: AssignTutorFields) => {
      if (!values?.file || uploading) {
        return;
      }
      setUploading(true);
      const { data, status } = await uploadData(values?.file);
      setUploading(false);

      switch (status) {
        case 200:
          addToast({
            icon: 'success',
            color: 'success',
            text: t(`${prefix}.success`),
          });
          !!onUpload && onUpload();
          setIsOpen(false);
          break;
        case 204:
          addToast({
            icon: 'error',
            color: 'danger',
            text: t(`${prefix}.empty`),
          });
          break;
        case 400:
          addToast({
            icon: 'error',
            color: 'danger',
            text: t(`${prefix}.tooLarge`),
          });
          break;

        default:
          if (data) {
            const blob = new Blob([data], { type: 'vnd.ms-excel' });
            downloadFromBlob(blob, `${t(`${prefix}.filename`)}.xlsx`);
          }
          addToast({
            icon: 'error',
            color: 'danger',
            text: t(`${prefix}.failed`),
          });
          break;
      }
    },
    [onUpload, uploading, t, prefix],
  );

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  return (
    <Modal toggle={close} isOpen={isOpen}>
      <div>
        <h1 className="text-dark text-center fw-600 fs-20">
          {t(`${prefix}.title`)}
        </h1>
        <p className=" text-center fs-16 lh-30 my-3">
          {t(`${prefix}.description`)}
        </p>
        <FileInputControlled
          btnText={t(`${prefix}.button`)}
          control={control}
          name="file"
          label={t(`${prefix}.label`)}
          rules={{ required: msgValidations.required }}
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        />
        <div
          className="d-flex flex-wrap justify-content-between mt-4"
          style={{ gap: '1rem' }}
        >
          <Button
            text={t(`${prefix}.close`)}
            outlined
            style={{ width: isMobile ? '100%' : 197 }}
            disabled={uploading}
            onClick={close}
          />
          <Button
            text={t(`${prefix}.upload`)}
            style={{ width: isMobile ? '100%' : 197 }}
            loading={uploading}
            onClick={handleSubmit(handleConfirm)}
          />
        </div>
      </div>
    </Modal>
  );
};

export default forwardRef(AssignTutorModal);
